/**
 * Backlog #52: the assistant that can do things. Streams the reply, shows Help Centre citations, and renders every
 * change as a preview card (mini rota grid, vehicle summary, …) with Apply / Cancel, then Undo where possible.
 * Nothing changes until Apply; rotas go in as drafts. Conversations are private to the person.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { create } from 'zustand';
import clsx from 'clsx';
import { AlertTriangle, Check, ChevronLeft, History, Info, Loader2, MessageSquarePlus, Pencil, Send, Sparkles, Trash2, Undo2, X } from 'lucide-react';
import {
  citationSegments, type AssistantAction, type AssistantChatMessage, type AssistantCitation, type AssistantPageContext, type AssistantPreview, type RotaGrid,
} from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { inlineMarks, tidyMarkdown } from '@/lib/assistantMarkdown';
import { queryClient } from '@/lib/queryClient';
import { useUi } from '@/lib/store';
import { Badge, Button, Spinner } from '@/components/ui';
import { MessageShiftsModal, type MessageShiftsRequest } from '@/features/workforce/messaging/MessageShiftsModal';
import { RelativeTime } from '@/components/time/RelativeTime';
import { useSourceLink } from './sourceLink';

interface Turn extends AssistantChatMessage { streaming?: boolean; step?: string | null; error?: string | null }
interface ChatState {
  conversationId: string | null; turns: Turn[]; busy: boolean;
  set: (p: Partial<ChatState>) => void; patch: (id: string, f: (t: Turn) => Turn) => void; reset: () => void;
}
/** Kept for the browser tab, so closing and reopening the panel doesn't lose the conversation. */
/** How each kind of source is labelled under an answer. */
const SOURCE_KIND: Record<string, string> = { guide: 'Help Centre guide', company: 'Company article', kb: 'Knowledge base', source: 'Workplace', schedule: 'Schedule', leave: 'Leave', record: 'Records' };

export const useActionChat = create<ChatState>((set) => ({
  conversationId: null, turns: [], busy: false,
  set: p => set(p),
  patch: (id, f) => set(s => ({ turns: s.turns.map(t => (t.id === id ? f(t) : t)) })),
  reset: () => set({ conversationId: null, turns: [], busy: false }),
}));

const EXAMPLES = ['How do I swap a shift?', 'Draft next week’s rota', 'Fill the open shifts this week', 'Add a van AB12 CDE, Ford Transit, diesel', 'Who’s working today?', 'Remind me to check the forklift on Friday'];

function usePageContext(): AssistantPageContext {
  const loc = useLocation();
  const schedule = useUi(s => s.assistantPage);
  return { path: loc.pathname + loc.search, title: document.title, schedule: /^\/(workforce\/)?schedule/.test(loc.pathname) ? schedule as AssistantPageContext['schedule'] : null };
}

export function useSendToAssistant() {
  const ws = useWorkspaceId();
  const page = usePageContext();
  return async (text: string) => {
    const st = useActionChat.getState();
    const q = text.trim();
    if (!q || !ws || st.busy) return;
    const uid = `u${Date.now()}`;
    const aid = `a${Date.now()}`;
    st.set({ busy: true, turns: [...st.turns, { id: uid, role: 'user', text: q, created_at: new Date().toISOString() }, { id: aid, role: 'assistant', text: '', created_at: new Date().toISOString(), streaming: true, actions: [], citations: [] }] });
    let cur = aid;
    const patch = (f: (t: Turn) => Turn) => useActionChat.getState().patch(cur, f);
    try {
      await api.assistantChat(ws, { message: q, conversation_id: st.conversationId, page }, e => {
        if (e.type === 'start') { useActionChat.getState().set({ conversationId: e.conversation_id }); patch(t => ({ ...t, id: e.message_id })); cur = e.message_id; }
        else if (e.type === 'text') patch(t => ({ ...t, text: t.text + e.delta, step: null }));
        else if (e.type === 'tool') patch(t => ({ ...t, step: e.label }));
        else if (e.type === 'action') patch(t => ({ ...t, actions: [...(t.actions ?? []), e.action] }));
        else if (e.type === 'citations') patch(t => ({ ...t, citations: e.citations }));
        else if (e.type === 'done') patch(t => ({ ...t, ...e.message, actions: t.actions?.length ? t.actions : e.message.actions, streaming: false, step: null }));
        else if (e.type === 'error') patch(t => ({ ...t, streaming: false, step: null, error: e.message }));
      });
    } catch (e) {
      patch(t => ({ ...t, streaming: false, step: null, error: errorMessage(e) }));
    } finally {
      patch(t => ({ ...t, streaming: false, step: null }));
      useActionChat.getState().set({ busy: false });
      void queryClient.invalidateQueries({ queryKey: ['ai-chat', ws] });
    }
  };
}

function Answer({ text, citations }: { text: string; citations: AssistantCitation[] }) {
  const byN = new Map(citations.map(c => [c.n, c]));
  const src = useSourceLink();
  let bold = false;
  return (
    <div className="whitespace-pre-wrap text-sm leading-relaxed text-slate-800 dark:text-slate-100">
      {citationSegments(tidyMarkdown(text), citations.length).map((seg, i) => {
        if (seg.kind === 'text') {
          const { parts, boldOut } = inlineMarks(seg.text, bold);
          bold = boldOut;
          return <span key={i}>{parts.map((p, j) => p.href
            ? <Link key={j} {...src.linkProps(p.href)} className={clsx('font-medium text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-700 dark:text-white', p.bold && 'font-semibold')} data-testid="assistant-link">{p.text}</Link>
            : p.code
            ? <code key={j} className={clsx('rounded bg-slate-100 px-1 text-[0.85em] dark:bg-slate-800', p.bold && 'font-semibold')}>{p.text}</code>
            : p.bold ? <strong key={j} className="font-semibold text-slate-900 dark:text-white">{p.text}</strong> : <span key={j}>{p.text}</span>)}</span>;
        }
        return (
          <Link key={i} {...src.linkProps(byN.get(seg.n)!.link)} title={byN.get(seg.n)!.title} data-testid="assistant-cite"
            className="mx-0.5 inline-flex h-4 min-w-4 -translate-y-0.5 items-center justify-center rounded bg-accent px-1 align-middle text-[10px] font-bold text-on-accent hover:bg-accent-hover">{seg.n}</Link>
        );
      })}
    </div>
  );
}

function Citations({ list }: { list: AssistantCitation[] }) {
  const src = useSourceLink();
  if (!list.length) return null;
  return (
    <div className="space-y-1">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Sources</div>
      {list.map(c => (
        <Link key={c.n} {...src.linkProps(c.link)} data-testid="assistant-source" className="flex items-start gap-1.5 rounded-lg border border-slate-200 p-2 text-xs hover:border-slate-400 dark:border-slate-800">
          <span className="flex h-4 min-w-4 items-center justify-center rounded bg-accent px-1 text-[10px] font-bold text-on-accent">{c.n}</span>
          <span className="min-w-0"><span className="block truncate font-semibold text-slate-800 dark:text-slate-100">{c.title}</span>
            <span className="text-slate-500">{SOURCE_KIND[c.kind] ?? 'Workplace'}</span></span>
        </Link>
      ))}
    </div>
  );
}

/** 06:00 → 6, 13:30 → 13:30 (as on the Schedule). */
const short = (t: string) => t.replace(/^0(\d)/, '$1').replace(/:00$/, '');

export function RotaGridCard({ grid }: { grid: RotaGrid }) {
  return (
    <div className="space-y-2">
      <div className="scroll-thin overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
        <table className="w-full min-w-[26rem] border-collapse text-[11px]" data-testid="rota-grid">
          <thead><tr className="bg-slate-50 text-slate-500 dark:bg-slate-900">
            <th className="sticky left-0 bg-slate-50 px-2 py-1 text-left font-semibold dark:bg-slate-900">Person</th>
            {grid.days.map(d => <th key={d} className="px-1 py-1 text-center font-semibold">{d}</th>)}
            <th className="px-2 py-1 text-right font-semibold">Hrs</th>
          </tr></thead>
          <tbody>
            {grid.rows.map(r => (
              <tr key={r.user_id ?? r.name} className="border-t border-slate-100 dark:border-slate-800">
                <td className="sticky left-0 max-w-[7rem] truncate bg-white px-2 py-1 font-medium dark:bg-slate-950">{r.name}</td>
                {r.cells.map(c => (
                  <td key={c.date} className="px-0.5 py-0.5 align-top">
                    {c.shifts.map((s, i) => (
                      <div key={i} title={s.position ?? undefined}
                        className={clsx('mb-0.5 whitespace-nowrap rounded px-1 py-0.5 text-center tabular-nums', s.is_new ? 'bg-accent font-semibold text-on-accent ring-1 ring-black/20' : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200')}>
                        {short(s.start)}–{short(s.end)}
                      </div>
                    ))}
                  </td>
                ))}
                <td className="px-2 py-1 text-right tabular-nums">{r.hours}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-3 text-[11px] text-slate-500">
        <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-4 rounded bg-accent ring-1 ring-black/20" />Added by this suggestion</span>
        <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-4 rounded bg-slate-100 dark:bg-slate-800" />Already on the rota</span>
      </div>
      {grid.gaps.length > 0 && (
        <div className="rounded-lg border border-slate-200 p-2 text-xs dark:border-slate-800">
          <div className="mb-1 font-semibold">Still open ({grid.gaps.length})</div>
          <ul className="space-y-0.5 text-slate-600 dark:text-slate-300">
            {grid.gaps.slice(0, 6).map((g, i) => <li key={i}><span className="font-medium">{g.date.slice(5).split('-').reverse().join('/')} {g.start}–{g.end}{g.position ? ` · ${g.position}` : ''}</span>: {g.reason}</li>)}
            {grid.gaps.length > 6 && <li>…and {grid.gaps.length - 6} more</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function Fields({ p }: { p: AssistantPreview }) {
  if (!p.fields?.length) return null;
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg border border-slate-200 p-2 text-xs dark:border-slate-800" data-testid="preview-fields">
      {p.fields.map(f => (
        <div key={f.label} className="contents">
          <dt className="text-slate-500">{f.label}</dt>
          <dd className="min-w-0 break-words font-medium">{f.before != null && f.before !== f.value ? <><s className="mr-1 text-slate-400">{f.before}</s>→ </> : null}{f.value}</dd>
        </div>
      ))}
    </dl>
  );
}

const STATUS: Record<AssistantAction['status'], string> = { proposed: 'Waiting for you', applied: 'Applied', cancelled: 'Cancelled', undone: 'Undone', failed: 'Didn’t work', expired: 'Expired' };

function ActionCard({ a, onChange, onEdit }: { a: AssistantAction; onChange: (a: AssistantAction) => void; onEdit?: (text: string) => void }) {
  const ws = useWorkspaceId();
  const src = useSourceLink();
  const toast = useUi(s => s.toast);
  const [busy, setBusy] = useState<string | null>(null);
  const [compose, setCompose] = useState<MessageShiftsRequest | null>(null);
  const p = a.preview;
  const run = async (what: 'apply' | 'cancel' | 'undo') => {
    setBusy(what);
    try {
      const r = what === 'apply' ? await api.applyAssistantAction(ws!, a.id) : what === 'cancel' ? await api.cancelAssistantAction(ws!, a.id) : await api.undoAssistantAction(ws!, a.id);
      onChange(r);
      if (r.status === 'failed') toast(r.error ?? 'That didn’t work', 'error');
      else if (what !== 'cancel') toast(r.result?.message ?? (what === 'undo' ? 'Undone' : 'Done'), 'success');
      const c = (r as { compose?: AssistantPreview['compose'] }).compose;
      if (c) setCompose({ source: 'selection', label: c.label, shift_ids: c.shift_ids, defaultTitle: c.label, initialBody: c.body, initialMode: c.mode });
      void queryClient.invalidateQueries({ queryKey: ['wf', ws] });
      void queryClient.invalidateQueries({ queryKey: ['fleet', ws] });
      void queryClient.invalidateQueries({ queryKey: ['tasks', ws] });
      void queryClient.invalidateQueries({ queryKey: ['tasks-area'] });
    } catch (e) { toast(errorMessage(e), 'error'); }
    setBusy(null);
  };
  const isMessage = p.kind === 'shift_message';
  return (
    <div className="space-y-2 rounded-xl border border-slate-300 bg-white p-3 shadow-sm dark:border-slate-700 dark:bg-slate-900" data-testid="assistant-action" data-kind={p.kind}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold">{p.title}</div>
          <div className="text-xs text-slate-600 dark:text-slate-300">{p.summary}</div>
        </div>
        <Badge className={a.status === 'proposed' ? '!bg-accent !text-on-accent' : undefined} color={a.status === 'applied' ? '#059669' : a.status === 'failed' ? '#e11d48' : a.status === 'proposed' ? undefined : '#64748b'}>{STATUS[a.status]}</Badge>
      </div>
      {p.grid && <RotaGridCard grid={p.grid} />}
      <Fields p={p} />
      {isMessage && p.compose && <div className="whitespace-pre-wrap rounded-lg bg-slate-50 p-2 text-xs dark:bg-slate-800">{p.compose.body}</div>}
      {p.warnings?.map((w, i) => <div key={`w${i}`} className="flex gap-1.5 text-xs text-orange-800 dark:text-orange-200"><AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{w}</div>)}
      {p.notes?.map((n, i) => <div key={`n${i}`} className="flex gap-1.5 text-xs text-slate-600 dark:text-slate-300"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />{n}</div>)}
      {a.error && <div className="rounded-md bg-rose-50 px-2 py-1 text-xs text-rose-700 dark:bg-rose-950/40 dark:text-rose-200">{a.error}</div>}
      {a.result?.message && a.status !== 'proposed' && (
        <div className="flex items-center gap-2 text-xs"><Check className="h-3.5 w-3.5 text-emerald-700" />{a.result.message}
          {a.result.link && a.status === 'applied' && <button className="font-semibold underline" onClick={() => src.open(a.result!.link!)}>Open</button>}</div>
      )}
      <div className="flex flex-wrap gap-2">
        {a.status === 'proposed' && <>
          <Button size="sm" onClick={() => void run('apply')} loading={busy === 'apply'} disabled={Boolean(busy)} data-testid="assistant-apply">
            <Check className="h-4 w-4" />{isMessage ? 'Open in compose' : p.kind === 'rota' || p.kind === 'shifts' ? 'Apply as drafts' : p.kind === 'shift' ? 'Create draft shift' : 'Apply'}
          </Button>
          {p.kind === 'shift' && onEdit && <Button size="sm" variant="secondary" data-testid="assistant-edit" onClick={() => { void run('cancel'); onEdit('Change that shift: '); }} disabled={Boolean(busy)}><Pencil className="h-4 w-4" />Edit</Button>}
          <Button size="sm" variant="secondary" onClick={() => void run('cancel')} disabled={Boolean(busy)}><X className="h-4 w-4" />Cancel</Button>
        </>}
        {a.status === 'applied' && a.can_undo && <Button size="sm" variant="secondary" onClick={() => void run('undo')} loading={busy === 'undo'} disabled={Boolean(busy)}><Undo2 className="h-4 w-4" />Undo</Button>}
      </div>
      {compose && <MessageShiftsModal request={compose} onClose={() => setCompose(null)} returnTo={{ label: 'Back to the assistant' }} />}
    </div>
  );
}

function HistoryList({ onBack }: { onBack: () => void }) {
  const ws = useWorkspaceId();
  const set = useActionChat(s => s.set);
  const q = useQuery({ queryKey: ['ai-chat', ws, 'conversations'], queryFn: () => api.assistantConversations(ws!), enabled: Boolean(ws) });
  const open = async (id: string) => {
    const c = await api.assistantConversation(ws!, id);
    set({ conversationId: c.id, turns: c.messages });
    onBack();
  };
  const del = async (id: string) => {
    await api.deleteAssistantConversation(ws!, id);
    if (useActionChat.getState().conversationId === id) useActionChat.getState().reset();
    void q.refetch();
  };
  return (
    <div className="scroll-thin min-h-0 flex-1 overflow-y-auto p-3">
      <button onClick={onBack} className="mb-2 flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 dark:hover:text-white"><ChevronLeft className="h-3.5 w-3.5" />Back to the chat</button>
      <p className="mb-2 text-xs text-slate-500">Only you can see your conversations.</p>
      {q.isLoading ? <Spinner /> : !(q.data?.conversations.length) ? <p className="text-sm text-slate-500">No conversations yet.</p> : (
        <ul className="divide-y divide-slate-100 dark:divide-slate-800">
          {q.data.conversations.map(c => (
            <li key={c.id} className="flex items-center gap-2 py-2">
              <button className="min-w-0 flex-1 text-left" onClick={() => void open(c.id)}>
                <span className="block truncate text-sm font-medium">{c.title}</span>
                <span className="text-xs text-slate-500"><RelativeTime iso={c.updated_at} /></span>
              </button>
              <button onClick={() => void del(c.id)} aria-label="Delete conversation" className="rounded p-1 text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ActionChat({ autoFocus }: { autoFocus?: boolean }) {
  const ws = useWorkspaceId();
  const { turns, busy, reset, patch } = useActionChat();
  const send = useSendToAssistant();
  const take = useUi(s => s.takeAssistantPending);
  const pending = useUi(s => s.assistantPending);
  const [q, setQ] = useState('');
  const [history, setHistory] = useState(false);
  const end = useRef<HTMLDivElement>(null);
  const status = useQuery({ queryKey: ['ai-chat', ws, 'status'], queryFn: () => api.assistantChatStatus(ws!), enabled: Boolean(ws) });
  const st = status.data;
  useEffect(() => { const p = take(); if (p) void send(p.question); }, [pending]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { end.current?.scrollIntoView({ block: 'end' }); }, [turns]);
  const submit = (e?: FormEvent) => { e?.preventDefault(); if (!q.trim() || busy) return; void send(q); setQ(''); };
  const updateAction = (msgId: string, a: AssistantAction) => patch(msgId, t => ({ ...t, actions: (t.actions ?? []).map(x => (x.id === a.id ? a : x)) }));

  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="action-chat">
      <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-2 text-xs dark:border-slate-800">
        {st && (st.demo
          ? <span className="rounded bg-accent px-1.5 py-0.5 font-semibold text-on-accent" title={st.reason ?? undefined}>Demo mode</span>
          : <Badge color="#0f172a"><Sparkles className="mr-1 inline h-3 w-3" />{st.model}</Badge>)}
        {st && <span className="text-slate-500">{Math.max(0, st.per_hour - st.used_this_hour)}/{st.per_hour} left this hour</span>}
        <div className="ml-auto flex items-center gap-1">
          <button onClick={() => setHistory(h => !h)} title="Your conversations" aria-label="Your conversations" className="rounded p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><History className="h-4 w-4" /></button>
          <button onClick={() => { reset(); setHistory(false); }} title="New conversation" aria-label="New conversation" className="rounded p-1 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><MessageSquarePlus className="h-4 w-4" /></button>
        </div>
      </div>
      {history ? <HistoryList onBack={() => setHistory(false)} /> : (
        <div className="scroll-thin min-h-0 flex-1 space-y-4 overflow-y-auto p-4" aria-live="polite">
          {st && !st.available && <div className="rounded-lg bg-slate-100 p-3 text-sm dark:bg-slate-800">{st.reason ?? 'The assistant is switched off.'}</div>}
          {!turns.length && (
            <div className="space-y-3 pt-4 text-center">
              <Sparkles className="mx-auto h-8 w-8 text-accent-fg" />
              <p className="text-sm font-medium">Ask how to do something, or ask me to set it up.</p>
              <p className="text-xs text-slate-500">I can draft rotas, add vehicles, plan journeys and create tasks. You’ll see a preview first, and nothing changes until you press Apply.</p>
              <div className="flex flex-wrap justify-center gap-1.5 pt-2">
                {EXAMPLES.map(x => <button key={x} onClick={() => void send(x)} className="rounded-full border border-slate-200 px-2.5 py-1 text-xs text-slate-600 hover:border-slate-400 dark:border-slate-700 dark:text-slate-300">{x}</button>)}
              </div>
            </div>
          )}
          {turns.map(t => t.role === 'user'
            ? <div key={t.id} className="ml-auto w-fit max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-accent px-3 py-2 text-sm text-on-accent">{t.text}</div>
            : (
              <div key={t.id} className="space-y-2" data-testid="assistant-reply">
                {t.text && <Answer text={t.text} citations={t.citations ?? []} />}
                {t.streaming && <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" />{t.step ?? 'Thinking…'}</div>}
                {t.error && <div className="rounded-md bg-rose-50 px-2.5 py-1.5 text-xs text-rose-700 dark:bg-rose-950/40 dark:text-rose-200">{t.error}</div>}
                {(t.actions ?? []).map(a => <ActionCard key={a.id} a={a} onChange={x => updateAction(t.id, x)} onEdit={text => { setQ(text); requestAnimationFrame(() => (document.querySelector('[aria-label="Message the assistant"]') as HTMLTextAreaElement | null)?.focus()); }} />)}
                {!t.streaming && <Citations list={t.citations ?? []} />}
              </div>
            ))}
          <div ref={end} />
        </div>
      )}
      <form onSubmit={submit} className="flex items-end gap-2 border-t border-slate-200 p-3 dark:border-slate-800">
        <textarea value={q} onChange={e => setQ(e.target.value)} rows={1} maxLength={4000} autoFocus={autoFocus} placeholder="Ask, or tell me what to set up…" aria-label="Message the assistant"
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); } }}
          className="max-h-32 min-h-9 flex-1 resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:focus:border-slate-300" />
        <Button type="submit" size="icon" disabled={!q.trim() || busy || (st && !st.available)} aria-label="Send"><Send className="h-4 w-4" /></Button>
      </form>
    </div>
  );
}
